import useResendReturnRequestEmail from "@babackapp/private-api-client/client/use-resend-return-request-email";
import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { MailIcon, SendIcon } from "lucide-react";
import { useState } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import ActivityContentWrapper from "./activity-content-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import ActivityWrapper from "./activity-wrapper";

type EmailActivityProps = {
  activity: ReturnRequestLogDto;
  returnRequestId: string;
};

const RESENDABLE_EMAIL_TYPES = new Set(["newLabel"]);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailActivity({
  activity,
  returnRequestId,
}: EmailActivityProps) {
  const t = useTranslation();
  const isSuperAdmin = useViewerIsSuperAdmin();
  const { data: shop } = useCurrentShopSuspense();
  const resendEmail = useResendReturnRequestEmail(shop?.id);

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("dev@baback.co");
  const [error, setError] = useState<string | null>(null);

  const emailNameByType = {
    newReturnRequest: t("returnDetail.requestCreation"),
    updateRequestStatus: t("returnDetail.requestUpdate"),
    newReturnRequestWithoutValidation: t(
      "returnDetail.newReturnRequestWithoutValidation",
    ),
    returnRequestValidated: t("returnDetail.returnRequestValidated"),
    discountCode: t("returnDetail.discountCode"),
    newReturnRequestWithoutLabel: t(
      "returnDetail.newReturnRequestWithoutLabel",
    ),
    newReturnRequestReturnToStore: t(
      "returnDetail.newReturnRequestReturnToStore",
    ),
    newLabel: t("returnDetail.newLabel"),
  };

  const canResend =
    isSuperAdmin &&
    !!activity.emailSent &&
    RESENDABLE_EMAIL_TYPES.has(activity.emailSent);

  const handleSubmit = async () => {
    if (!EMAIL_REGEX.test(email)) {
      setError(t("activities.email.resendInvalidEmail"));
      return;
    }
    setError(null);
    try {
      await resendEmail.mutateAsync({
        returnRequestId,
        logId: activity.id,
        email,
      });
      setOpen(false);
      setEmail("dev@baback.co");
    } catch {
      // Error toast is already surfaced by the mutation hook.
    }
  };

  return (
    <ActivityWrapper>
      <ActivityIconWrapper>
        <MailIcon className="size-4" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <div className="flex items-center gap-2">
          <ActivityItemHeader
            title={t("activities.email.title")}
            timestamp={activity.date}
          >
            <span className="font-medium text-xs underline">
              {emailNameByType[
                activity.emailSent as keyof typeof emailNameByType
              ] || ""}
            </span>
          </ActivityItemHeader>
        </div>
        {canResend && (
          <Dialog open={open} onOpenChange={setOpen}>
            <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <span className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-0.5 font-medium">
                Baback CSM
              </span>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 transition-colors"
                >
                  <SendIcon className="h-4 w-4" />
                  {t("activities.email.resend")}
                </button>
              </DialogTrigger>
            </div>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t("activities.email.resendDialogTitle")}
                </DialogTitle>
              </DialogHeader>
              <Input
                type="email"
                value={email}
                placeholder={t("activities.email.resendPlaceholder")}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <DialogFooter>
                <Button onClick={handleSubmit} disabled={resendEmail.isPending}>
                  {t("activities.email.resendSubmit")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
